// src/components/SmartAssignmentLogic.js
import { db } from "../firebase";
import {
  collection,
  query,
  where,
  limit,
  getDocs,
  onSnapshot,
  doc,
  writeBatch,
  serverTimestamp,
} from "firebase/firestore";
import { useEffect } from "react";

const generateAccessKey = () => Math.random().toString(36).substring(2, 8).toUpperCase();

export default function SmartAssignmentLogic() {
  const processRegistration = async (registrationDoc) => {
    const registrationData = registrationDoc.data();
    const regId = registrationDoc.id;

    if (!registrationData || registrationData.roomAssignmentStatus !== "Pending") return;

    const batch = writeBatch(db);

    try {
      const roomsQuery = query(
        collection(db, `hotels/${registrationData.hotelId}/rooms`),
        where("status", "==", "Vacant_Clean"),
        where("maxOccupancy", ">=", registrationData.numberOfGuests || 1),
        limit(1)
      );

      const roomsSnapshot = await getDocs(roomsQuery);

      if (roomsSnapshot.empty) {
        const regRef = doc(db, "registrations", regId);
        batch.update(regRef, {
          roomAssignmentStatus: "Assignment Failed",
          failureReason: "No clean room matching occupancy criteria.",
          updatedAt: serverTimestamp(),
        });
        await batch.commit();
        return;
      }

      const roomDoc = roomsSnapshot.docs[0];
      const assignedRoom = roomDoc.data();
      const assignedRoomId = roomDoc.id;
      const accessKey = generateAccessKey();

      const checkInTime = new Date(registrationData.arrivalTime).getTime();
      const checkOutTime = new Date(registrationData.departureTime).getTime();
      const totalDays = Math.max(1, Math.ceil((checkOutTime - checkInTime) / (1000 * 60 * 60 * 24)));
      const roomPrice = assignedRoom.price || 0;
      const totalPrice = totalDays * roomPrice;

      const guestData = {
        fullName: registrationData.fullName,
        email: registrationData.email,
        phone: registrationData.phone,
        bookingSource: registrationData.bookingSource,
        country: (registrationData.address || "").split(",").pop()?.trim() || "Unknown",
        hotelId: registrationData.hotelId,
        roomNumber: assignedRoom.roomNumber,
        roomId: assignedRoomId,
        roomPrice,
        accessKey,
        checkInDate: registrationData.arrivalTime,
        checkOutDate: registrationData.departureTime,
        totalDays,
        totalPrice,
        status: "CHECKED_IN",
        paymentMethod: "Pre-Registered",
        checkInTimestamp: serverTimestamp(),
        originalRegistrationId: regId,
        signatureDataUrl: registrationData.signatureDataUrl || null,
        checkedInBy: "System/Digital",
      };

      const guestRef = doc(collection(db, "guests"));
      batch.set(guestRef, guestData);
      const newGuestId = guestRef.id;

      const roomRef = doc(db, `hotels/${registrationData.hotelId}/rooms`, assignedRoomId);
      batch.update(roomRef, {
        status: "Occupied",
        currentGuestId: newGuestId,
      });

      const regRef = doc(db, "registrations", regId);
      batch.update(regRef, {
        roomAssignmentStatus: "Assigned",
        assignedRoomNumber: assignedRoom.roomNumber,
        assignedGuestId: newGuestId,
        accessKey,
        updatedAt: serverTimestamp(),
      });

      await batch.commit();
      console.log(`[SmartLogic] Assigned ${assignedRoom.roomNumber} -> ${registrationData.fullName}`);
    } catch (error) {
      console.error(`[SmartLogic] Error processing registration ${regId}`, error);
    }
  };

  useEffect(() => {
    const q = query(collection(db, "registrations"), where("roomAssignmentStatus", "==", "Pending"));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      snapshot.docChanges().forEach((change) => {
        if ((change.type === "added" || change.type === "modified") && change.doc.data().roomAssignmentStatus === "Pending") {
          processRegistration(change.doc);
        }
      });
    });

    return () => unsubscribe();
  }, []);

  return null;
}
